import { Component, computed, DestroyRef, EventEmitter, input, OnInit, Output } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { PaymentFacilitatorType, PaymentService, RetailStatus, Transaction } from '@galaxy/billing';
import { MatPaginatorNoTotalDisplayed } from '@vendasta/va-filter2-table';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { TransactionFilters } from './transactions-table/transactions-table-datasource';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AsyncPipe, NgIf } from '@angular/common';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { TransactionsTableComponent } from './transactions-table/transactions-table.component';
import { RetailPaymentsOnboardingComponent } from '../retail-payments/onboarding/retail-payments-onboarding.component';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { DisputesPageStatsService } from '../disputes/disputes-page-stats.service';

@Component({
  selector: 'smb-invoicing-transactions',
  templateUrl: './transactions.component.html',
  styleUrls: ['./transactions.component.scss'],
  providers: [{ provide: MatPaginatorIntl, useClass: MatPaginatorNoTotalDisplayed }],
  standalone: true,
  imports: [
    AsyncPipe,
    NgIf,
    TranslateModule,
    GalaxyAlertModule,
    TransactionsTableComponent,
    RetailPaymentsOnboardingComponent,
  ],
})
export class TransactionsComponent implements OnInit {
  merchantId = input.required<string>();
  paymentFacilitatorType = input<PaymentFacilitatorType>();
  invoiceId = input<string>();
  hideTableActions = input<boolean>(false);
  displayedColumns = input<string[]>([
    'description',
    'customerId',
    'created',
    'payment-method',
    'status',
    'amount',
    'fee',
    'net',
    'actions',
  ]);
  hasEmptyStateSecondaryCallToAction = input<boolean>(true);
  canClickTransactionReference = input<boolean>(true);
  displayTableBorder = input<boolean>(true);
  customerTitleTranslationKey = input<string>('PAYMENTS_PAGE.CUSTOMER');

  @Output() issueCreditNote = new EventEmitter<string>();
  @Output() customerClicked = new EventEmitter<string>();
  @Output() transactionReferenceClicked = new EventEmitter<Transaction>();
  @Output() transactionClicked = new EventEmitter<Transaction>();
  @Output() emptyStatePrimaryCallToAction = new EventEmitter<boolean>();
  @Output() emptyStateSecondaryCallToAction = new EventEmitter<boolean>();
  @Output() onboardingStateContactSupportAction: EventEmitter<boolean> = new EventEmitter();
  @Output() onboardingStateSetupVendastaPaymentsAction: EventEmitter<boolean> = new EventEmitter();

  loading$$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  supportedRegion$: Observable<boolean>;
  canAcceptPayment$: Observable<boolean>;
  hasActiveDisputes$: Observable<boolean>;
  retailStatus$: Observable<RetailStatus>;

  paymentsFilters = computed(() => (this.invoiceId() ? ({ invoiceId: this.invoiceId() } as TransactionFilters) : {}));

  constructor(
    private paymentService: PaymentService,
    private destroyRef: DestroyRef,
    private router: Router,
    private disputePageStatsService: DisputesPageStatsService,
  ) {
    this.retailStatus$ = toObservable(this.merchantId).pipe(
      switchMap((id) => this.paymentService.retailStatus(id)),
      catchError((_) => {
        return of({ supportedRegion: false, canAcceptPayment: false });
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.hasActiveDisputes$ = toObservable(this.merchantId).pipe(
      switchMap((pid) => this.disputePageStatsService.get(pid)),
      map((resp) => resp.countInquiryRequiresResponse > 0 || resp.countRequiresResponse > 0),
    );
  }

  ngOnInit(): void {
    this.supportedRegion$ = this.retailStatus$.pipe(map((rs) => rs.supportedRegion));
    this.canAcceptPayment$ = this.retailStatus$.pipe(map((rs) => rs.canAcceptPayment));
    this.retailStatus$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => this.loading$$.next(false));
  }

  issueCreditNoteHandler(referenceId: string): void {
    this.issueCreditNote.emit(referenceId);
  }

  customerClickedHandler(customerId: string): void {
    this.customerClicked.emit(customerId);
  }

  transactionReferenceClickedHandler(transaction: Transaction): void {
    this.transactionReferenceClicked.emit(transaction);
  }

  transactionClickedHandler(transaction: Transaction): void {
    this.transactionClicked.emit(transaction);
  }

  emptyStatePrimaryCallToActionHandler(): void {
    this.emptyStatePrimaryCallToAction.emit(true);
  }

  emptyStateSecondaryCallToActionHandler(): void {
    this.emptyStateSecondaryCallToAction.emit(true);
  }

  openDisputeActionClicked(): void {
    this.router.navigateByUrl('/billing/disputes?tab=needs-response');
  }

  onboardingStateContactSupportActionClicked(event: boolean): void {
    if (!event) {
      return;
    }
    this.onboardingStateContactSupportAction.emit(event);
  }

  onboardingStateSetupVendastaPaymentsActionClicked(event: boolean): void {
    if (!event) {
      return;
    }
    this.onboardingStateSetupVendastaPaymentsAction.emit(event);
  }
}
